import VueWrapper from '@/components/core/Vue/vue.wrapper';
import {AutentictionApi} from '@/sdk/api-services';
import {PasswordResetRequestModel, PasswordResetVerifyModel} from '@/sdk/models';
import {Component} from 'vue-property-decorator';

@Component
export default class ForgotPasswordComponent extends VueWrapper {
    public loginId: string = '';
    public phoneNumber: string = '';
    public verificationCodeDigits: string[] = ['', '', '', '', '', ''];
    public hasCodeBeenSent: boolean = false;

    public resendAvailableAt: Date | null = null;
    public resendCountdown: number = 0;

    private resendTimer: number | null = null;

    public authenticationApi: AutentictionApi = new AutentictionApi();

    public created() {
        this.loginId = (this.$route.query.loginId as string) ?? '';
    }

    public beforeDestroy() {
        if (this.resendTimer) {
            clearInterval(this.resendTimer);
            this.resendTimer = null;
        }
    }

    public get resendDisabled() {
        return this.resendCountdown > 0;
    }

    public get sendCodeButtonText() {
        return this.hasCodeBeenSent ? 'Resend code' : 'Send code';
    }

    public get isVerificationCodeComplete() {
        return this.verificationCodeDigits.every(digit => /^\d$/.test(digit));
    }

    public get maskedPhone() {
        if (!this.phoneNumber) {
            return '';
        }
        const last4 = this.phoneNumber.slice(-4);
        return `${this.phoneNumber.slice(0, -4).replace(/\d/g, '*')}${last4}`;
    }

    public async requestCode() {
        if (this.resendDisabled) {
            return;
        }

        if (!this.loginId) {
            this.AlertSrv.show('error', 'Login ID is required.');
            return;
        }

        if (!this.phoneNumber) {
            this.AlertSrv.show('error', 'Mobile phone is required.');
            return;
        }

        try {
            this.LoaderSrv.showFullScreenLoader('Sending code...');
            const payload = new PasswordResetRequestModel({
                loginId: this.loginId,
                phoneNumber: this.phoneNumber,
            });
            const res = await this.authenticationApi.RequestPasswordResetCode(payload);
            this.resendAvailableAt = new Date(res.resendAvailableAt);
            this.hasCodeBeenSent = true;
            this.startResendCountdown();
            this.AlertSrv.show('success', 'Verification code sent.');
        } catch (err: any) {
            this.AlertSrv.show('error', err?.error?.message ?? 'Unable to send verification code.');
        } finally {
            this.LoaderSrv.hideFullScreenLoader();
        }
    }

    public async verifyCodeAndContinue() {
        if (!this.isVerificationCodeComplete) {
            this.AlertSrv.show('error', 'Please enter the 6-digit verification code.');
            return;
        }

        try {
            this.LoaderSrv.showFullScreenLoader('Verifying code...');
            const payload = new PasswordResetVerifyModel({
                loginId: this.loginId,
                phoneNumber: this.phoneNumber,
                code: this.verificationCodeDigits.join(''),
            });
            const res = await this.authenticationApi.VerifyPasswordResetCode(payload);

            this.$router.push({
                name: 'resetPassword',
                query: {
                    token: res.resetToken,
                    loginId: this.loginId,
                },
            });
        } catch (err: any) {
            this.AlertSrv.show('error', err?.error?.message ?? 'Verification failed. Please try again.');
        } finally {
            this.LoaderSrv.hideFullScreenLoader();
        }
    }

    public goToSignin() {
        this.$router.push({name: 'signin'});
    }

    public handleCodeInput(index: number, value: string) {
        const numericOnly = (value ?? '').replace(/\D/g, '');
        if (!numericOnly) {
            this.verificationCodeDigits.splice(index, 1, '');
            return;
        }

        if (numericOnly.length > 1) {
            const digits = numericOnly.slice(0, 6).split('');
            for (let i = 0; i < 6; i++) {
                this.verificationCodeDigits.splice(i, 1, digits[i] ?? '');
            }
            this.focusCodeInput(Math.min(digits.length, 5));
            return;
        }

        this.verificationCodeDigits.splice(index, 1, numericOnly);
        if (index < 5) {
            this.focusCodeInput(index + 1);
        }
    }

    public handleCodeKeydown(index: number, event: KeyboardEvent) {
        if (event.key === 'Backspace' && !this.verificationCodeDigits[index] && index > 0) {
            this.focusCodeInput(index - 1);
        }

        if (event.key === 'ArrowLeft' && index > 0) {
            this.focusCodeInput(index - 1);
        }

        if (event.key === 'ArrowRight' && index < 5) {
            this.focusCodeInput(index + 1);
        }
    }

    public handleCodePaste(event: ClipboardEvent) {
        const pasted = event.clipboardData?.getData('text') ?? '';
        const digits = pasted.replace(/\D/g, '').slice(0, 6).split('');
        if (!digits.length) {
            return;
        }

        event.preventDefault();
        for (let i = 0; i < 6; i++) {
            this.verificationCodeDigits.splice(i, 1, digits[i] ?? '');
        }
        this.focusCodeInput(Math.min(digits.length, 5));
    }

    private startResendCountdown() {
        if (!this.resendAvailableAt) {
            return;
        }

        this.updateResendCountdown();
        if (this.resendTimer) {
            clearInterval(this.resendTimer);
        }
        this.resendTimer = window.setInterval(() => this.updateResendCountdown(), 1000);
    }

    private updateResendCountdown() {
        if (!this.resendAvailableAt) {
            this.resendCountdown = 0;
            return;
        }

        const diffMs = this.resendAvailableAt.getTime() - Date.now();
        this.resendCountdown = diffMs > 0 ? Math.ceil(diffMs / 1000) : 0;
        if (this.resendCountdown <= 0 && this.resendTimer) {
            clearInterval(this.resendTimer);
            this.resendTimer = null;
        }
    }

    private focusCodeInput(index: number) {
        const refKey = `verificationDigit${index}`;
        const target = (this.$refs[refKey] as any[] | any)?.[0] ?? this.$refs[refKey];
        if (target?.focus) {
            target.focus();
        }
    }
}
